import {
  APP_HEADER_BACKGROUND_COLOR,
  AppBarStyles,
} from '@client/style/style.constants';
import {
  useIsProjectList,
  useIsSharedProjectRoute,
} from '@client/hooks/use-router.hooks';
import { useEffect, useMemo, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

export enum AppBarElementId {
  ArrowBackIcon = 'arrow-back-icon',
  NodonLogo = 'nodon-logo',
  ProjectSelector = 'project-selector',
  ProjectSelectorContainer = 'project-selector-container',
}

type OpenState = Partial<Record<AppBarElementId, boolean>>;

export const useAppbarStyles = () => {
  const isProjectList = useIsProjectList();
  const isSharedProject = useIsSharedProjectRoute();
  const [isOpen, setIsOpen] = useState<OpenState>();

  useEffect(() => {
    setIsOpen({ [AppBarElementId.ProjectSelector]: isProjectList });
  }, [isProjectList]);

  const { classes } = useStyles({ isOpen, isSharedProject, isProjectList });
  return useMemo(() => ({ classes, setIsOpen }), [classes, setIsOpen]);
};

const useStyles = makeStyles<{
  isOpen?: OpenState;
  isSharedProject?: boolean;
  isProjectList?: boolean;
}>()((theme, { isOpen, isSharedProject, isProjectList }) => {
  return {
    appBar: {
      displayPrint: 'none',
      '--project-list-header-surface-opacity':
        'clamp(0, calc((240px - var(--project-list-app-header-height, 240px)) / 192px), 1)',
      backgroundColor: isProjectList
        ? `color-mix(in srgb, ${APP_HEADER_BACKGROUND_COLOR} calc(var(--project-list-header-surface-opacity) * 80%), transparent)`
        : APP_HEADER_BACKGROUND_COLOR,
      color: '#1c1c1c',
      borderBottom: isProjectList
        ? '1px solid rgba(0,0,0,calc(var(--project-list-header-surface-opacity) * 0.1))'
        : '1px solid rgba(0,0,0,0.1)',
      boxShadow: isProjectList
        ? '0px 1px 10px 0px rgba(0,0,0,calc(var(--project-list-header-surface-opacity) * 0.1))'
        : '0px 1px 10px 0px rgba(0,0,0,0.1)',
    },
    toolbar: {
      flexDirection: 'column',
      justifyContent: 'center',
      // MUI Toolbar has minHeight: 56px/64px via breakpoints — must use !important to override.
      minHeight: isProjectList
        ? `var(--project-list-app-header-height, ${AppBarStyles.HEIGHT * 5}px) !important`
        : `${AppBarStyles.HEIGHT}px !important`,
      height: isProjectList
        ? `var(--project-list-app-header-height, ${AppBarStyles.HEIGHT * 5}px)`
        : AppBarStyles.HEIGHT,
      padding: 0,

      [`.${AppBarElementId.NodonLogo}`]: { display: 'block' },
      [`.${AppBarElementId.ArrowBackIcon}`]: { display: 'none' },

      [`&:hover .${AppBarElementId.ArrowBackIcon}`]: {
        display:
          isSharedProject || isOpen?.[AppBarElementId.ProjectSelector]
            ? 'none'
            : 'flex',
      },
      [`&:hover .${AppBarElementId.NodonLogo}`]: {
        display:
          isSharedProject || isOpen?.[AppBarElementId.ProjectSelector]
            ? 'block'
            : 'none',
      },
    },
    projectSelectorContainer: {
      [`.${AppBarElementId.ArrowBackIcon}`]: {
        color: 'rgba(0,0,0,0.6)',
      },
      [`&:hover .${AppBarElementId.ArrowBackIcon}`]: {
        color: 'rgba(0,0,0,1)',
      },
    },
  };
});
